/**
 * Public market feeds (Terminal, Markets & Pricing, homepage market rows):
 * paths, validation and the browser-side reader. Same JSON shapes as the
 * canideal.org endpoints. Server pages read them with ISR through api.ts
 * (server-only API key); the browser re-reads them on the .org cadence via
 * readPublicFeed(). Kept beside api.ts (like toolsApi.ts) so client bundles
 * stay small and both files stay under 500 lines.
 */
import { APP_BASE } from '@/lib/constants';
import { isObject } from '@/lib/json';
import type { DailyMarketDataPayload, DailyNationalSeriesRow, FeedRow } from '@/lib/types';

export const MARKET_FEED_PATHS = {
  terminal: '/api/market/public/terminal',
  terminalBoard: '/api/market/public/terminal-board',
  marketsPricing: '/api/market/public/markets-pricing',
  dailyMarketData: '/api/market/public/daily-market-data',
} as const;

/** Feeds refresh every few hours upstream; the .org pages re-read every 5 minutes. */
export const MARKET_FEED_REVALIDATE = 300;

/** Upper bound for a feed body (canideal.org assets/homepage/market-data.js). */
export const MARKET_FEED_MAX_BYTES = 1024 * 1024;

const FEED_STATUSES = new Set([
  'current',
  'partial',
  'unavailable',
  'available',
  'withdrawn',
  'delayed',
  // Daily market data with no current series (canideal.org server.js publishes it too).
  'refresh_overdue',
]);

/** Accepts a feed object whose status is one of the published states. */
export function isFeedPayload(value: unknown): value is { status: string } {
  return isObject(value) && typeof value.status === 'string' && FEED_STATUSES.has(value.status);
}

/**
 * National rows of a daily-data payload, filtered exactly like canideal.org
 * daily-market-data.js: US-state rows (those with `state`) and the United States
 * aggregate are regional context, never national series; malformed rows drop.
 */
export function nationalDailySeries(
  payload: DailyMarketDataPayload | null
): DailyNationalSeriesRow[] {
  if (!payload || !Array.isArray(payload.series)) return [];
  return payload.series.filter(
    (row): row is FeedRow & DailyNationalSeriesRow =>
      Boolean(row) &&
      typeof row === 'object' &&
      !row.state &&
      row.country !== 'United States' &&
      typeof row.country === 'string' &&
      typeof row.period === 'string' &&
      typeof row.value === 'number' &&
      Number.isFinite(row.value)
  );
}

export interface FeedRead {
  ok: boolean;
  status: number;
  payload: unknown;
}

/**
 * On Vercel preview hosts (*.vercel.app), browser requests use the same-origin
 * Next.js rewrite (`/api/market/public/:path*`) so cross-origin CORS never blocks
 * preview deployments.
 */
function publicFeedBase(): string {
  const host = (globalThis as { window?: { location?: { hostname?: string } } }).window?.location
    ?.hostname;
  if (typeof host === 'string' && /\.vercel\.app$/i.test(host)) return '';
  return APP_BASE;
}

/**
 * Browser read of a public feed (no cookies, no cache). Throws
 * `transport_unavailable` (network, timeout, non-JSON) or `invalid_feed`
 * (oversized or unparseable body); an error status with a JSON body resolves
 * so callers can honour a published `unavailable` state.
 */
export async function readPublicFeed(
  path: string,
  { signal, timeoutMs = 15000 }: { signal?: AbortSignal; timeoutMs?: number } = {}
): Promise<FeedRead> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort);
  const timeout = setTimeout(abort, timeoutMs);
  try {
    let response: Response;
    try {
      response = await fetch(`${publicFeedBase()}${path}`, {
        cache: 'no-store',
        credentials: 'omit',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
    } catch {
      throw new Error('transport_unavailable');
    }
    if (!/^application\/json\b/i.test(response.headers.get('content-type') || '')) {
      throw new Error('transport_unavailable');
    }
    if (Number(response.headers.get('content-length')) > MARKET_FEED_MAX_BYTES) {
      throw new Error('invalid_feed');
    }
    let text: string;
    try {
      text = await response.text();
    } catch {
      throw new Error('transport_unavailable');
    }
    if (text.length > MARKET_FEED_MAX_BYTES) throw new Error('invalid_feed');
    try {
      return { ok: response.ok, status: response.status, payload: JSON.parse(text) };
    } catch {
      throw new Error('invalid_feed');
    }
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', abort);
  }
}
